/**
 * Deployed-agents collection — typed CRUD on top of the generic store.
 *
 * A DeployedAgent is a named, configured Claude worker launched from
 * inside the app (Projects → Agents). This adapter owns the template
 * catalogue, the deploy path, lifecycle transitions (run / pause /
 * resume), and a "run once" that appends a believable run entry and
 * bumps the run counter — the same shape the ticket autopilot uses, so
 * a deployed ticket-worker reads as the in-app face of that loop.
 * Modelled on `tasks.ts`.
 */

import { db, nowIso, uuid } from './store';
import { logActivity } from './activity';
import type {
  AgentCadence,
  AgentRunEntry,
  AgentRunState,
  AgentTemplateId,
  DeployedAgent,
} from './types';
import { seedAgents } from './seed';
import { scanSystem } from '../agent/systemScanner';
import { proposeFollowUps } from '../agent/followUpAgent';
import { proposeRevenueTasks } from '../agent/revenueAgent';
import { proposeTicketTriage } from '../agent/ticketTriageAgent';
import { proposeTaskRouting } from '../agent/taskRouterAgent';
import { proposeHealthCheckins } from '../agent/customerHealthAgent';
import { proposeDocumentChases, documentChaseRunText } from '../agent/documentChaseAgent';
import { appendFeedEntry, runSummary, type RunStats } from '../agent/activityFeed';
import { nextLiveRunIso } from '../agent/liveSchedule';
import * as argoWorker from '../agent/argoWorker';

/**
 * A deployable blueprint. The deploy modal renders one card per
 * template; picking one pre-fills the agent's persona, cadence, and
 * description, which the user can then tweak before launching.
 */
export interface AgentTemplate {
  id: AgentTemplateId;
  label: string;
  /** One-liner shown on the template card. */
  blurb: string;
  /** Autopilot persona this template binds to. */
  persona: string;
  defaultCadence: AgentCadence;
  /** Pre-filled description for the deployed instance. */
  description: string;
  /** Short narration fragment used when the agent "runs". */
  runVerb: string;
}

export const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    id: 'ticket-worker',
    label: 'Ticket worker',
    blurb: 'Claims open tickets and works them through to review.',
    persona: 'claude:worker',
    defaultCadence: 'continuous',
    description:
      'Sweeps the ticket queue, claims unowned work that matches its skills, ' +
      'and advances it one step at a time toward review.',
    runVerb: 'swept the ticket queue and advanced in-flight work',
  },
  {
    id: 'researcher',
    label: 'Researcher',
    blurb: 'Investigates spikes and writes up findings.',
    persona: 'claude:researcher',
    defaultCadence: 'manual',
    description:
      'Picks up research and investigation tickets, gathers findings, and ' +
      'posts a written analysis for sign-off.',
    runVerb: 'gathered findings and updated its open investigations',
  },
  {
    id: 'outreach-monitor',
    label: 'Outreach monitor',
    blurb: 'Watches prospect activity and flags follow-ups.',
    persona: 'claude:worker',
    defaultCadence: 'daily',
    description:
      'Scans prospects for stale follow-ups and overdue next-steps, then ' +
      'logs the ones that need a touch.',
    runVerb: 'scanned prospects and flagged stale follow-ups',
  },
  {
    id: 'system-scanner',
    label: 'System scanner',
    blurb: 'Surfaces overdue, at-risk, and stalled items.',
    persona: 'claude:researcher',
    defaultCadence: 'hourly',
    description:
      'Sweeps tickets, tasks, and accounts for overdue deadlines, blocked ' +
      'work, and stalled deals, and surfaces the important ones.',
    runVerb: 'scanned the system and surfaced items needing attention',
  },
  {
    id: 'customer-success',
    label: 'Customer success',
    blurb: 'Scores live-customer health and proposes check-ins for slipping accounts.',
    persona: 'claude:worker',
    defaultCadence: 'daily',
    description:
      'Scores every live customer\u2019s health from their real record \u2014 renewal ' +
      'proximity, past-due billing, touch recency, blocked deliverables, and overdue ' +
      'tasks \u2014 and proposes a check-in task for any account that has slipped into ' +
      'the watch or at-risk band.',
    runVerb: 'scored customer health and flagged slipping accounts',
  },
  {
    id: 'document-chase',
    label: 'Document chaser',
    blurb: 'Chases lapsed and stalled proposals/contracts back into motion.',
    persona: 'claude:worker',
    defaultCadence: 'daily',
    description:
      'Reads the documents collection for proposals and contracts the client is ' +
      'sitting on \u2014 lapsed past their validity (still inflating open pipeline) or ' +
      'awaiting the client with no movement \u2014 and proposes a chase task tied to the ' +
      'document\u2019s own account or contact: re-issue/close a lapsed doc, nudge an awaiting one.',
    runVerb: 'chased lapsed and stalled documents',
  },
  // --- Ingestion / intelligence -----------------------------------------
  {
    id: 'market-research',
    label: 'Market research',
    blurb: 'Tracks competitors, pricing, and market shifts.',
    persona: 'claude:researcher',
    defaultCadence: 'daily',
    description:
      'Monitors the competitive landscape — competitor moves, pricing changes, ' +
      'funding news, and category shifts — and writes up what changed and why it matters.',
    runVerb: 'scanned the market and logged competitive movements',
  },
  {
    id: 'icp-builder',
    label: 'ICP builder',
    blurb: 'Synthesizes ideal-customer profiles from CRM signal.',
    persona: 'claude:researcher',
    defaultCadence: 'manual',
    description:
      'Aggregates won deals, prospect attributes, and engagement signal to build ' +
      'and refine ideal-customer profiles and buyer personas.',
    runVerb: 'refined the ICP from current pipeline signal',
  },
  {
    id: 'web-scraper',
    label: 'Web scraper',
    blurb: 'Pulls and structures data from target websites.',
    persona: 'claude:researcher',
    defaultCadence: 'manual',
    description:
      'Visits target company and prospect websites, extracts firmographic and ' +
      'product signal, and structures it into the CRM as enrichment.',
    runVerb: 'scraped target sites and structured the findings',
  },
  {
    id: 'linkedin-monitor',
    label: 'LinkedIn monitor',
    blurb: 'Watches prospect LinkedIn activity for buying signals.',
    persona: 'claude:researcher',
    defaultCadence: 'daily',
    description:
      'Tracks prospect and account LinkedIn activity — role changes, posts, ' +
      'hiring — and flags buying signals worth a timely touch.',
    runVerb: 'monitored LinkedIn and flagged fresh buying signals',
  },
  {
    id: 'x-monitor',
    label: 'X monitor',
    blurb: 'Tracks mentions and conversations on X.',
    persona: 'claude:researcher',
    defaultCadence: 'daily',
    description:
      'Watches X for brand mentions, competitor chatter, and prospect activity, ' +
      'and surfaces threads worth engaging with.',
    runVerb: 'tracked X conversations and surfaced relevant threads',
  },
  {
    id: 'six-sigma-analyst',
    label: 'Six Sigma analyst',
    blurb: 'Finds process variance and improvement opportunities.',
    persona: 'claude:researcher',
    defaultCadence: 'hourly',
    description:
      'Applies a Six Sigma lens to operational data — cycle times, win rates, ' +
      'ticket throughput — to find variance, bottlenecks, and improvement opportunities.',
    runVerb: 'analyzed process metrics and flagged variance',
  },
  // --- Content -----------------------------------------------------------
  {
    id: 'content-writer',
    label: 'Content writer',
    blurb: 'Drafts on-brand posts, emails, and proposals.',
    persona: 'claude:worker',
    defaultCadence: 'manual',
    description:
      'Drafts on-brand content — blog posts, sales emails, one-pagers, and proposal ' +
      'sections — from a brief and the surrounding CRM context.',
    runVerb: 'drafted on-brand content from the latest briefs',
  },
  {
    id: 'outreach-sequencer',
    label: 'Outreach sequencer',
    blurb: 'Builds and paces multi-step outreach sequences.',
    persona: 'claude:worker',
    defaultCadence: 'continuous',
    description:
      'Builds multi-step outreach sequences per prospect, paces the touches, and ' +
      'queues the next step as each one lands.',
    runVerb: 'advanced active outreach sequences',
  },
  // --- ClickUp-style ops -------------------------------------------------
  {
    id: 'task-router',
    label: 'Task router',
    blurb: 'Routes incoming work to the right owner or agent.',
    persona: 'claude:worker',
    defaultCadence: 'continuous',
    description:
      'Triages incoming tickets and tasks, matches them to the right owner or agent ' +
      'by skill and load, and assigns them.',
    runVerb: 'triaged the queue and routed work to owners',
  },
  {
    id: 'deadline-watcher',
    label: 'Deadline watcher',
    blurb: 'Watches due dates and escalates at-risk work.',
    persona: 'claude:researcher',
    defaultCadence: 'hourly',
    description:
      'Watches due dates across tickets, tasks, deliverables, and billing, and ' +
      'escalates anything overdue or at risk of slipping.',
    runVerb: 'checked deadlines and escalated at-risk work',
  },
  {
    id: 'standup-reporter',
    label: 'Standup reporter',
    blurb: 'Compiles a daily standup of progress and blockers.',
    persona: 'claude:researcher',
    defaultCadence: 'daily',
    description:
      'Compiles a daily standup — what moved, what shipped, what is blocked — ' +
      'across tickets, tasks, and agents into one digest.',
    runVerb: 'compiled the daily standup digest',
  },
  {
    id: 'custom',
    label: 'Custom agent',
    blurb: 'Start from scratch and define your own scope.',
    persona: 'claude:worker',
    defaultCadence: 'manual',
    description: '',
    runVerb: 'ran its configured job',
  },
];

const TEMPLATE_BY_ID: Record<AgentTemplateId, AgentTemplate> = Object.fromEntries(
  AGENT_TEMPLATES.map((t) => [t.id, t]),
) as Record<AgentTemplateId, AgentTemplate>;

/** Look up a template blueprint by id. */
export function agentTemplate(id: AgentTemplateId): AgentTemplate {
  return TEMPLATE_BY_ID[id] ?? TEMPLATE_BY_ID.custom;
}

const entry = (
  kind: AgentRunEntry['kind'],
  actor: string,
  text: string,
  at = nowIso(),
): AgentRunEntry => ({ id: uuid(), at, actor, kind, text });

export async function listAgents(): Promise<DeployedAgent[]> {
  return db.list('agents');
}

export async function getAgent(id: string): Promise<DeployedAgent | null> {
  return db.get('agents', id);
}

export function subscribeAgents(fn: (records: DeployedAgent[]) => void): () => void {
  return db.subscribe('agents', fn);
}

export interface DeployAgentInput {
  name: string;
  template: AgentTemplateId;
  persona?: string;
  description?: string;
  cadence?: AgentCadence;
  createdBy?: string;
}

/**
 * Deploy a new agent. A `continuous` cadence starts `running`; anything
 * else starts `idle` until it's run (manually or on schedule). Pulls
 * persona / cadence / description defaults from the chosen template when
 * the caller leaves them blank.
 */
export async function deployAgent(input: DeployAgentInput): Promise<DeployedAgent> {
  const ts = nowIso();
  const tpl = agentTemplate(input.template);
  const createdBy = input.createdBy ?? 'You';
  const cadence = input.cadence ?? tpl.defaultCadence;
  const record: DeployedAgent = {
    id: uuid(),
    name: input.name.trim() || tpl.label,
    template: input.template,
    persona: input.persona ?? tpl.persona,
    description: (input.description ?? tpl.description).trim(),
    cadence,
    state: cadence === 'continuous' ? 'running' : 'idle',
    createdBy,
    lastRunAt: null,
    runCount: 0,
    timeline: [entry('deployed', createdBy, `Deployed from ${tpl.label} template`, ts)],
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('agents', record);
  return record;
}

/** Patch an agent. Re-stamps `updatedAt`. */
export async function updateAgent(
  id: string,
  patch: Partial<Omit<DeployedAgent, 'id' | 'createdAt' | 'timeline'>>,
): Promise<DeployedAgent> {
  return db.update('agents', id, { ...patch, updatedAt: nowIso() });
}

/** Flip an agent's lifecycle state with a `status` log entry. */
export async function setAgentState(
  id: string,
  state: AgentRunState,
  actor = 'You',
): Promise<DeployedAgent> {
  const existing = await db.get('agents', id);
  if (!existing) throw new Error(`No agent ${id}`);
  if (existing.state === state) return existing;
  const ts = nowIso();
  return db.update('agents', id, {
    state,
    timeline: [
      ...existing.timeline,
      entry('status', actor, `State: ${existing.state} → ${state}`, ts),
    ],
    updatedAt: ts,
  });
}

/**
 * Run an agent once: append a run entry (attributed to the agent's
 * persona), stamp `lastRunAt`, and bump `runCount`. A `continuous` agent
 * stays `running`; anything else settles to `idle` after the run.
 */
export interface AgentRunResult extends DeployedAgent {
  /**
   * How many proposals this run queued into the approvals inbox, derived
   * honestly by counting pending approvals before and after the run. Lets
   * the UI give real feedback ("N proposals queued") without re-deriving it
   * from the run narration. Extends DeployedAgent so existing callers that
   * read agent fields (e.g. the scheduler) keep working unchanged.
   */
  queued: number;
}

export async function runAgentOnce(
  id: string,
  opts: { trigger?: 'schedule' | 'manual' } = {},
): Promise<AgentRunResult> {
  const existing = await db.get('agents', id);
  if (!existing) throw new Error(`No agent ${id}`);
  const ts = nowIso();
  const tpl = agentTemplate(existing.template);
  // Snapshot the pending inbox before the run so we can honestly report how
  // many proposals THIS run added, regardless of which producer ran.
  const pendingBefore = (await db.list('approvals')).filter(
    (a) => a.status === 'pending',
  ).length;

  // A system-scanner agent actually runs the scan engine and reports
  // what it found, so its run history is a real audit of the sweep.
  let runText = `Ran — ${tpl.runVerb}`;
  // Real counts for the "Argo did this" feed — set by every branch that
  // runs a real producer, so the feed line reports what the run actually
  // scanned / found / queued. Stays null for simulated-narration templates.
  let stats: RunStats | null = null;
  // The system scanner does REAL work: it sweeps every collection for a full
  // situational summary AND runs the revenue producer, which queues
  // `create_task` proposals for the pressing money events (past-due billing,
  // payments due soon, approaching renewals) into the approvals inbox. Domains
  // are split so it never overlaps the outreach monitor's follow-ups: the
  // scanner owns revenue tasks, the monitor owns follow-ups. De-dupe inside
  // `proposeRevenueTasks` keeps repeated runs from piling duplicate tasks.
  if (existing.template === 'system-scanner') {
    const [tickets, tasks, people, accounts] = await Promise.all([
      db.list('tickets'),
      db.list('tasks'),
      db.list('people'),
      db.list('accounts'),
    ]);
    const { summary } = scanSystem({ tickets, tasks, people, accounts });
    const { proposed, skipped } = await proposeRevenueTasks(people);
    stats = {
      scanned: tickets.length + tasks.length + people.length + accounts.length,
      found: summary.total,
      queued: proposed,
    };
    const swept =
      summary.total === 0
        ? 'swept the system, nothing needs attention'
        : `surfaced ${summary.total} item${summary.total === 1 ? '' : 's'} ` +
          `(${summary.high} high · ${summary.medium} medium · ${summary.low} low)`;
    if (proposed > 0 || skipped > 0) {
      const bits: string[] = [];
      if (proposed > 0)
        bits.push(`proposed ${proposed} revenue task${proposed === 1 ? '' : 's'} for review`);
      if (skipped > 0) bits.push(`${skipped} already queued`);
      runText = `Ran — ${swept} · ${bits.join(' · ')}`;
    } else {
      runText = `Ran — ${swept}`;
    }
  }

  // The outreach monitor does REAL work: it runs the follow-up producer,
  // which scans drifting prospects and queues `schedule_follow_up` proposals
  // into the approvals inbox for operator review. Both this scheduled path and
  // the manual "Run now" button flow through here, so a deployed-and-running
  // outreach monitor fills the inbox on its cadence — gated by approval, never
  // auto-writing. De-dupe inside `proposeFollowUps` keeps repeated runs from
  // piling duplicate proposals for the same prospect.
  if (existing.template === 'outreach-monitor') {
    const people = await db.list('people');
    const { proposed, skipped } = await proposeFollowUps(people);
    stats = { scanned: people.length, found: proposed + skipped, queued: proposed };
    if (proposed === 0 && skipped === 0) {
      runText = 'Ran — scanned prospects, nothing drifting';
    } else {
      const bits: string[] = [];
      if (proposed > 0)
        bits.push(`proposed ${proposed} follow-up${proposed === 1 ? '' : 's'} for review`);
      if (skipped > 0) bits.push(`${skipped} already awaiting approval`);
      runText = `Ran — ${bits.join(' · ')}`;
    }
  }

  // The ticket worker does REAL work: it runs the ticket-triage producer,
  // which scans the queue for overdue work that never started and queues a
  // `set_ticket_status` proposal (→ in-progress) for each into the approvals
  // inbox. Both this scheduled path and the manual "Run now" button flow
  // through here, so a deployed-and-running ticket worker escalates stalled
  // overdue tickets on its cadence — gated by approval, never auto-writing. It
  // owns the ticket queue, distinct from the monitor's follow-ups and the
  // scanner's revenue tasks. De-dupe inside `proposeTicketTriage` keeps
  // repeated runs from piling duplicates for the same ticket.
  if (existing.template === 'ticket-worker') {
    const tickets = await db.list('tickets');
    const { proposed, skipped } = await proposeTicketTriage(tickets);
    stats = { scanned: tickets.length, found: proposed + skipped, queued: proposed };
    if (proposed === 0 && skipped === 0) {
      runText = 'Ran — swept the queue, nothing overdue to escalate';
    } else {
      const bits: string[] = [];
      if (proposed > 0)
        bits.push(`proposed ${proposed} ticket escalation${proposed === 1 ? '' : 's'} for review`);
      if (skipped > 0) bits.push(`${skipped} already awaiting approval`);
      runText = `Ran — ${bits.join(' · ')}`;
    }
  }

  // The task router does REAL work: it runs the task-routing producer, which
  // scans the queue for unowned high-priority tickets and queues an
  // `assign_ticket` proposal for each — routing it to the human carrying the
  // lightest load — into the approvals inbox. Both this scheduled path and the
  // manual "Run now" button flow through here, so a deployed-and-running router
  // balances the queue on its cadence — gated by approval, never auto-writing.
  // It owns *ownership*, distinct from the ticket worker's *status* moves, the
  // monitor's follow-ups, and the scanner's revenue tasks. De-dupe inside
  // `proposeTaskRouting` keeps repeated runs from piling duplicate assignments.
  if (existing.template === 'task-router') {
    const tickets = await db.list('tickets');
    const { proposed, skipped } = await proposeTaskRouting(tickets);
    stats = { scanned: tickets.length, found: proposed + skipped, queued: proposed };
    if (proposed === 0 && skipped === 0) {
      runText = 'Ran — triaged the queue, every priority ticket already owned';
    } else {
      const bits: string[] = [];
      if (proposed > 0)
        bits.push(`proposed ${proposed} assignment${proposed === 1 ? '' : 's'} for review`);
      if (skipped > 0) bits.push(`${skipped} already awaiting approval`);
      runText = `Ran — ${bits.join(' · ')}`;
    }
  }

  // The customer-success agent does REAL work: it runs the health producer,
  // which scores every live customer from their real record and queues a
  // `create_task` check-in proposal for each account that has slipped into the
  // watch / at-risk band — carrying the account's ARR as the dollars at risk.
  // Both this scheduled path and the manual "Run now" button flow through here,
  // so a deployed-and-running CS agent keeps us on top of every live customer
  // on its cadence — gated by approval, never auto-writing. It owns the
  // *relationship* domain, distinct from the scanner's revenue tasks and the
  // monitor's prospect follow-ups. De-dupe inside `proposeHealthCheckins` keeps
  // repeated runs from piling duplicate check-ins for the same account.
  if (existing.template === 'customer-success') {
    const [people, tasks] = await Promise.all([db.list('people'), db.list('tasks')]);
    const { proposed, skipped } = await proposeHealthCheckins(people, tasks);
    stats = {
      scanned: people.length + tasks.length,
      found: proposed + skipped,
      queued: proposed,
    };
    if (proposed === 0 && skipped === 0) {
      runText = 'Ran — scored customer health, every live account is healthy';
    } else {
      const bits: string[] = [];
      if (proposed > 0)
        bits.push(`proposed ${proposed} check-in${proposed === 1 ? '' : 's'} for review`);
      if (skipped > 0) bits.push(`${skipped} already awaiting approval`);
      runText = `Ran — ${bits.join(' · ')}`;
    }
  }

  // The document chaser does REAL work: it runs the document-chase producer,
  // which reads the documents collection for lapsed (past-validity, still
  // counted as open pipeline) and awaiting-client docs and queues a
  // `create_task` chase proposal for each — tied to the document's own account
  // or contact — into the approvals inbox. Both this scheduled path and the
  // manual "Run now" button flow through here, so a deployed-and-running chaser
  // keeps stale documents moving on its cadence — gated by approval, never
  // auto-writing. It owns the *document lifecycle* domain, distinct from the
  // scanner's revenue tasks and the CS agent's relationship check-ins (separate
  // title namespaces, so the shared `create_task` de-dupe never collides them).
  // De-dupe inside `proposeDocumentChases` keeps repeated runs from piling
  // duplicate chases for the same document.
  if (existing.template === 'document-chase') {
    const documents = await db.list('documents');
    const outcome = await proposeDocumentChases(documents);
    stats = {
      scanned: documents.length,
      found: outcome.proposed + outcome.skipped,
      queued: outcome.proposed,
    };
    runText = documentChaseRunText(outcome);
  }

  // The deadline watcher does a REAL read-only sweep: it runs the scan
  // engine across every deadline-bearing collection and reports what it
  // found — surfaced findings live on the Dashboard's "needs attention"
  // feed. It queues nothing itself (the revenue / triage producers own
  // their domains), so its feed line honestly reports 0 proposals.
  if (existing.template === 'deadline-watcher') {
    const [tickets, tasks, people, accounts] = await Promise.all([
      db.list('tickets'),
      db.list('tasks'),
      db.list('people'),
      db.list('accounts'),
    ]);
    const { summary } = scanSystem({ tickets, tasks, people, accounts });
    const scanned = tickets.length + tasks.length + people.length + accounts.length;
    stats = { scanned, found: summary.total, queued: 0 };
    runText =
      summary.total === 0
        ? `Ran — checked ${scanned} records for deadline risk, nothing at risk`
        : `Ran — checked ${scanned} records for deadline risk, surfaced ` +
          `${summary.total} finding${summary.total === 1 ? '' : 's'} needing attention`;
  }

  // When the hosted Argo agent-worker is configured, the agent does real
  // work through the AWS → Claude pipeline and we log what it actually did.
  // If the worker is absent or the call fails we keep the believable
  // simulation narration above, so the run history never goes blank.
  if (argoWorker.isConfigured()) {
    try {
      const [tickets, tasks, people, accounts] = await Promise.all([
        db.list('tickets'),
        db.list('tasks'),
        db.list('people'),
        db.list('accounts'),
      ]);
      const out = await argoWorker.runAgent(
        {
          id: existing.id,
          name: existing.name,
          template: existing.template,
          persona: existing.persona,
        },
        { tickets, tasks, people, accounts },
      );
      runText = out.text;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Argo agent] worker run failed, using simulated narration:', err);
    }
  }

  // Record the run in the governance activity log so agent activity is real
  // and auditable — this is what ends the "0 audit entries despite N runs"
  // contradiction. Best-effort: logging must never break the run it records.
  await logActivity({
    event: 'agent_run',
    summary: `${existing.name}: ${runText}`,
    source: 'agent',
    kind: existing.template,
    subjectId: id,
    at: ts,
  }).catch(() => undefined);

  const updated = await db.update('agents', id, {
    state: existing.cadence === 'continuous' ? 'running' : 'idle',
    lastRunAt: ts,
    // Persisted so agent cards can show a live "next run" without
    // re-deriving cadence math; null for manual cadence.
    nextRunAt: nextLiveRunIso(existing.cadence, ts),
    runCount: existing.runCount + 1,
    timeline: [...existing.timeline, entry('run', existing.persona, runText, ts)],
    updatedAt: ts,
  });

  const pendingAfter = (await db.list('approvals')).filter(
    (a) => a.status === 'pending',
  ).length;
  const queued = Math.max(0, pendingAfter - pendingBefore);

  // The "Argo did this" Home feed — appended HERE so every run path
  // (live schedule, global autonomy loop, human "Run now") reports itself
  // with the run's REAL count (honest before/after diff, not narration).
  // Best-effort: a feed write must never break the run it records.
  await appendFeedEntry({
    agentId: existing.id,
    agentName: existing.name,
    summary: stats ? runSummary(stats) : runText.replace(/^Ran — /, ''),
    proposalsQueued: queued,
    trigger: opts.trigger ?? 'manual',
    ts,
  }).catch(() => undefined);

  return { ...updated, queued };
}

export async function deleteAgent(id: string): Promise<void> {
  await db.remove('agents', id);
}

/** Seed the agents collection on first run. Idempotent. */
export async function bootstrapAgentsIfEmpty(): Promise<void> {
  const existing = await db.list('agents');
  if (existing.length > 0) return;
  await db.replaceAll('agents', seedAgents);
}

/** Reset the agents collection back to seed data. Useful for dev. */
export async function resetAgentsToSeed(): Promise<void> {
  await db.replaceAll('agents', seedAgents);
}
